import { cn } from "@/lib/utils";
import { initials, brl, brlShort } from "@/lib/format";
import { Loader2, Inbox, AlertTriangle, Info as InfoIcon } from "@/components/icons";
import HelpTip from "@/components/HelpTip";

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-navy">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Avatar({ name, src, size = 40, className }) {
  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center bg-brand-soft text-brand-hover font-semibold overflow-hidden shrink-0 border border-brand/10",
        className
      )}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {src ? (
        <img src={src} alt={name} className="w-full h-full object-cover" />
      ) : (
        initials(name)
      )}
    </div>
  );
}

export function KpiCard({ label, value, rawValue, sub, icon: Icon, tone = "brand", onClick, testId, help }) {
  const tones = {
    brand: "text-brand bg-brand-soft",
    ok: "text-ok bg-ok-soft",
    warn: "text-warn bg-warn-soft",
    bad: "text-bad bg-bad-soft",
    navy: "text-navy bg-navy-soft",
    info: "text-info bg-info-soft",
    neutral: "text-[#62717F] bg-[#F3F8FB]",
  };
  // UI-1 (item 2) — número NUNCA quebra (nowrap), fonte com clamp fluido,
  // valor completo em `title` para toque/hover. Se `rawValue` for número,
  // mostra formato curto no celular e completo no computador.
  const temRaw = typeof rawValue === "number" && Number.isFinite(rawValue);
  const valorMobile = temRaw ? brlShort(rawValue) : value;
  const valorDesktop = value;
  const valorFull = temRaw ? brl(rawValue) : (typeof value === "string" ? value : null);
  return (
    <div
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "bg-card rounded-xl border border-black/5 p-4 sm:p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] transition-all duration-300 min-w-0",
        onClick && "cursor-pointer hover:-translate-y-1 hover:shadow-[0_10px_30px_rgb(0,0,0,0.07)]"
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
          {label}
          {help && (
            <HelpTip label={help}>
              <button type="button" onClick={(e) => e.stopPropagation()} className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-muted-foreground hover:text-navy" data-testid={testId ? `${testId}-info` : "kpi-info"} aria-label="O que significa este número?">
                <InfoIcon className="w-3.5 h-3.5" />
              </button>
            </HelpTip>
          )}
        </span>
        {Icon && (
          <span className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", tones[tone])}>
            <Icon className="w-4 h-4" />
          </span>
        )}
      </div>
      <div
        className="font-semibold text-navy mt-3 tracking-tight tabular-nums whitespace-nowrap overflow-hidden text-ellipsis"
        style={{ fontSize: "clamp(1.05rem, 5.2vw, 1.75rem)", lineHeight: 1.1 }}
        title={valorFull || undefined}
      >
        <span className="sm:hidden">{valorMobile}</span>
        <span className="hidden sm:inline">{valorDesktop}</span>
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1 whitespace-nowrap overflow-hidden text-ellipsis">{sub}</div>}
    </div>
  );
}

export function Loading({ label = "Carregando..." }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-3">
      <Loader2 className="w-7 h-7 animate-spin text-brand" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-slate-400" />
      </div>
      <h3 className="text-base font-semibold text-navy">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-md">{subtitle}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ onRetry, message = "Não foi possível carregar os dados." }) {
  return (
    <EmptyState
      icon={AlertTriangle}
      title="Algo deu errado"
      subtitle={message}
      action={
        onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand-hover transition-colors"
            data-testid="retry-btn"
          >
            Tentar novamente
          </button>
        )
      }
    />
  );
}

export function Skeleton({ className }) {
  return <div className={cn("animate-pulse rounded-lg bg-slate-200/70", className)} />;
}

export function DashboardSkeleton() {
  return (
    <div>
      <Skeleton className="h-8 w-56 mb-2" />
      <Skeleton className="h-4 w-40 mb-6" />
      <Skeleton className="h-16 w-full rounded-xl mb-6" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <Skeleton className="lg:col-span-5 h-48 rounded-2xl" />
        <div className="lg:col-span-7 grid grid-cols-2 gap-5">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="lg:col-span-7 h-64 rounded-2xl" />
        <Skeleton className="lg:col-span-5 h-64 rounded-2xl" />
      </div>
    </div>
  );
}

export function ListSkeleton({ rows = 5 }) {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-16 rounded-xl" />
      ))}
    </div>
  );
}
